import React, {FC, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {Box, Grid, Typography, useTheme} from '@mui/material'
import {enqueueSnackbar} from 'notistack'

import {ReactComponent as AccountCompleteImage} from '@progyny/assets/icons/big-check.svg'
import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import {Navigator} from '@progyny/components/Navigator'
import {useCreateAccountMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useHeap} from '@progyny/hooks/useHeap'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

const Complete: FC = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const user = useMeStore((s) => s.user)
  const getErrorMessage = useErrorCodes()
  const [createAccount] = useCreateAccountMutation()
  const membersName = user?.member.preferredGivenName || user?.firstName
  const {sendHeapData} = useHeap()
  const {getPath} = useRoutes()
  const theme = useTheme()
  const isMobile = useResponsive('down', 'md')

  useEffect(() => {
    const heapTimeoutId = sendHeapData()
    return () => clearTimeout(heapTimeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleJourney = () => {
    if (user?.member?.availableProductLines?.length! > 1) {
      history.push(getPath(RouteEnum.SELECT_JOURNEY))
    } else {
      createAccount({
        variables: {
          memberJourney: user?.member?.availableProductLines?.[0],
        },
      })
        .then(({data}) => {
          if (data && data.createAccount) {
            if (data.createAccount.success) {
              history.push(getPath(RouteEnum.SETUP_PROFILE))
            } else if (data?.createAccount?.errors) {
              const errors = data?.createAccount?.errors
              const message = errors[0]?.messages[0]
              return enqueueSnackbar({
                message: getErrorMessage(message as string),
                variant: 'error',
              })
            } else {
              const errorCode = data.createAccount.errorCode
              enqueueSnackbar({
                message: getErrorMessage(errorCode as string),
                variant: 'error',
              })
            }
          }
        })
        .catch((err: any) => {
          console.error(err)
          enqueueSnackbar({
            message: t('profile_bank_details_cannot_reach_server'),
            variant: 'error',
          })
          return
        })
    }
  }

  return (
    <Grid
      container
      direction={'column'}
      justifyContent={'center'}
      sx={{pb: {xs: MOBILE_STICKY_HEIGHT, md: 0}}}
    >
      <Grid item xs={12} md={9}>
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <Box mb={4}>
            <Typography variant={'h4'} textAlign={'center'}>
              {t('create_account_youre_all_set', {
                membersName,
              })}
            </Typography>
          </Box>
          <Box mb={6}>
            <Box
              sx={{
                background: theme.palette.neutral[50],
                borderRadius: '100%',
                height: 100,
                width: 100,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <AccountCompleteImage fontSize={60} />
            </Box>
          </Box>
          <Box>
            <Grid container justifyContent={'center'}>
              <Grid item xs={12} sm={11}>
                <Typography
                  variant={'body1'}
                  textAlign={'center'}
                  lineHeight={1.8}
                >
                  {t('create_account_complete_let_s_start_by_answering')}
                </Typography>
              </Grid>
            </Grid>
            <MobileStickyActions>
              <Navigator
                label={t('create_account_complete_begin_my_journey')}
                showBackButton={false}
                onNextButtonClick={() => handleJourney()}
                singleCentered
                fullWidth={isMobile}
              />
            </MobileStickyActions>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default Complete
